/* eslint-disable no-continue */
import { buffer, extend } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import { easeIn } from 'ol/easing';
import { hasObjectKeys } from '../main';
import { removeLayerIfExists } from './layers';
import { removeOverlayIfExists } from './overlays';
import { ZOOMING_ANIMATION_UAVO_DURATION } from '../../consts/mapConfig';
import { getFeatureByIdFromLayer } from '../../components/uavos/utils/common';

export const addLayerToMap = (map, layer) => {
  if (!Object.keys(map).length || !layer) return;
  removeLayerIfExists(map, layer);
  map.addLayer(layer);
};

export const addOverlayToMap = (map, overlay) => {
  if (!overlay) return;
  removeOverlayIfExists(map, overlay);
  map.addOverlay(overlay);
};

export const removeComponentLayers = map => {
  if (!hasObjectKeys(map)) return;

  const layersWhichStay = ['tileLayer', 'jurysdictionLayer', 'transmissionTowersLayer'];

  const layersToRemove = [...map.getLayers().getArray()];
  for (const layer of layersToRemove) {
    if (
      !layersWhichStay.find(
        layerName => layerName === layer.getProperties().layerName
      )
    )
      map.removeLayer(layer);
  }
};

export const zoomToExtentWithBuffer = (map, extent, eBuffer = 7000) => {
  const extentWithBuffor = buffer(extent, eBuffer);
  map.getView().fit(extentWithBuffor, {
    duration: ZOOMING_ANIMATION_UAVO_DURATION,
    easeIn,
  });
};

export const zoomToExtentWithoutBuffer = (map, extent) => {
  map.getView().fit(extent, {
    duration: ZOOMING_ANIMATION_UAVO_DURATION,
    padding: [350, 350, 350, 350],
    easeIn,
  });
};

export const zoomToFeatureWithBuffer = (map, feature, eBuffer = 7000) => {
  if (!feature) return;

  const extent = feature?.getGeometry()?.getExtent();
  if (extent) {
    zoomToExtentWithBuffer(map, extent, eBuffer);
  }
};

export const zoomToGeojsonGeometryWithBuffer = (
  map,
  geojson,
  eBuffer = 7000
) => {
  if (!geojson) return;

  let extent = null;
  if (geojson.feature || geojson.features) {
    const features = new GeoJSON().readFeatures(geojson, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857',
    });
    if (!features) return;
    for (const feature of features) {
      const newExtent = feature.getGeometry().getExtent();
      if (!newExtent) continue;
      if (extent) extent = extend(extent, newExtent);
      else extent = newExtent;
    }
  } else {
    const geometry = new GeoJSON().readGeometry(geojson, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857',
    });
    if (geometry) extent = geometry.getExtent();
  }

  if (extent) {
    return geojson.type === 'FeatureCollection'
      ? zoomToExtentWithoutBuffer(map, extent)
      : zoomToExtentWithBuffer(map, extent, eBuffer);
  }
};

export const removeFeatureByIdIfExist = (layer, featureId) => {
  if (!layer.getSource()) return;
  const feature = getFeatureByIdFromLayer(layer, featureId);
  if (feature) layer.getSource().removeFeature(feature);
};

export const getAllClickedFeatures = (map, event) => {
  if (!hasObjectKeys(map)) return;
  return map.getFeaturesAtPixel(event.pixel);
};

export const getAllFeaturesByDetail = (allFeatures = [], detailName) => {
  const searchedFeatures = [];
  for (const feature of allFeatures) {
    if (feature.getProperties().detail === detailName)
      searchedFeatures.push(feature);
  }
  return searchedFeatures;
};

export const getGeojsonGeometryFromFeature = feature => {
  try {
    const json =
      JSON.parse(
        new GeoJSON().writeGeometry(feature.getGeometry(), {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857',
        })
      ) || null;

    return parseGeometryFrom3dTo2d(json);
  } catch (e) {
    return null;
  }
};

export const parseGeometryFrom3dTo2d = geometry => {
  if (geometry.coordinates[0][0].length === 3) {
    const parsedCoords = [];
    for (const coords of geometry.coordinates[0]) {
      parsedCoords.push([coords[0], coords[1]]);
    }

    return { ...geometry, coordinates: [parsedCoords] };
  }
  if (geometry.type === 'LineString' && geometry.coordinates[0].length === 3) {
    const parsedCoords = [];
    for (const coords of geometry.coordinates) {
      parsedCoords.push([coords[0], coords[1]]);
    }

    return { ...geometry, coordinates: parsedCoords };
  }
  return geometry;
};
